import { type Dispatch, type SetStateAction } from "react";

import OpenInNewIcon from "@mui/icons-material/OpenInNew";
import { Alert, AlertTitle, Box, Button, Link, Stack, TextField, Typography } from "@mui/material";

import { useTestConnection } from "../hooks";
import { type FormState } from "../types";

type Props = {
  formState: FormState;
  onChange: Dispatch<SetStateAction<FormState>>;
};

export default function ClientCredentialsStep({ formState, onChange }: Props) {
  const { testConnection } = useTestConnection();

  const handleChange = (field: "clientId" | "clientSecret", value: string) => {
    onChange({
      ...formState,
      [field]: value,
      connectionSuccess: false,
      connectionMessage: "",
    });
  };

  const handleTestConnection = async () => {
    if (!formState.clientId || !formState.clientSecret) {
      return;
    }

    onChange((prevState) => ({
      ...prevState,
      loading: true,
      connectionSuccess: false,
      connectionMessage: "",
    }));

    const testResult = await testConnection(formState.orgId, {
      clientId: formState.clientId,
      clientSecret: formState.clientSecret,
    });

    // Update form state with test results
    onChange((prevState) => ({
      ...prevState,
      loading: false,
      connectionSuccess: testResult.success,
      connectionMessage: testResult.message,
    }));
  };

  const isTestDisabled = !formState.clientId || !formState.clientSecret || formState.loading;

  return (
    <Stack data-cy="client-credentials-step" spacing={4}>
      <Typography variant="h3">Enter your Wiz Client Credentials</Typography>

      <Stack spacing={2}>
        <Typography variant="body1" fontWeight="medium">
          Follow these steps to create a service account in Wiz:
        </Typography>

        <Box component="ol" sx={{ pl: 2, m: 0 }}>
          <Typography component="li" variant="body1" sx={{ mb: 1 }}>
            Login to&nbsp;
            <Link
              href="https://app.wiz.io"
              target="_blank"
              data-cy="wiz-login-link"
              display="inline-flex"
              underline="none"
              sx={{ alignItems: "center", gap: 0.5 }}
            >
              Wiz
              <OpenInNewIcon fontSize="inherit" />
            </Link>
            &nbsp;with the Project Admin role
          </Typography>
          <Typography component="li" variant="body1" sx={{ mb: 1 }}>
            Navigate to Settings {"> "}Access Management folder {"> "}{" "}
            <Link
              sx={{
                alignItems: "center",
                display: "inline-flex",
                gap: 0.5,
              }}
              color="primary"
              variant="body1"
              underline="none"
              target="_blank"
              href="https://app.wiz.io/settings/service-accounts"
              data-cy="wiz-service-accounts-link"
            >
              Service Accounts
              <OpenInNewIcon fontSize="inherit" />
            </Link>{" "}
            {"> "} Add Service Account
          </Typography>
          <Typography component="li" variant="body1" sx={{ mb: 1 }}>
            Enter a Service Account Name
          </Typography>
          <Typography component="li" variant="body1" sx={{ mb: 1 }}>
            Select the <em>Custom Integration (GraphQL API)</em> from the Type dropdown.
          </Typography>
          <Typography component="li" variant="body1" sx={{ mb: 1 }}>
            Select the projects with the resources you wish to pull the issues.
          </Typography>
          <Typography component="li" variant="body1" sx={{ mb: 1 }}>
            Select only the <em>read:issues</em> permission in the API Scopes and click <em>Add account</em>
          </Typography>
        </Box>
      </Stack>

      <Stack spacing={3}>
        <TextField
          label="Client ID"
          fullWidth
          value={formState.clientId}
          required
          onChange={(event) => {
            handleChange("clientId", event.target.value);
          }}
          placeholder="Enter your Wiz Client ID"
          data-cy="client-id-input"
        />

        <TextField
          label="Client Secret"
          fullWidth
          type="password"
          value={formState.clientSecret}
          required
          onChange={(event) => {
            handleChange("clientSecret", event.target.value);
          }}
          placeholder="Enter your Wiz Client Secret"
          data-cy="client-secret-input"
        />
      </Stack>

      <Stack spacing={2}>
        <Typography variant="body1">
          To make sure the setup is successful, we must test the connection. This should take a couple of seconds.
        </Typography>
        <Button
          variant="outlined"
          sx={{ width: "140px" }}
          onClick={handleTestConnection}
          disabled={isTestDisabled}
          loading={formState.loading}
          data-cy="test-connection-button"
        >
          Test connection
        </Button>

        {formState.connectionMessage && !formState.connectionSuccess && (
          <Alert severity="error" data-cy="connection-test-error">
            <Stack>
              <AlertTitle>Test failed</AlertTitle>
              {formState.connectionMessage}
            </Stack>
          </Alert>
        )}

        {formState.connectionMessage && formState.connectionSuccess && (
          <Alert severity="success" data-cy="connection-test-success">
            <AlertTitle>Test successful</AlertTitle>
            <Typography variant="body1">{formState.connectionMessage}</Typography>
          </Alert>
        )}
      </Stack>
    </Stack>
  );
}
